// Single source of truth for the legal-document version users have accepted.
//
// `LEGAL_VERSION` is the version string that gates re-acceptance. When ANY of
// the conditions below are met for either platform-docs/legal/TERMS_OF_USE.md
// or platform-docs/legal/PRIVACY_POLICY.md, bump LEGAL_VERSION and follow the
// PHASE 2 PROCEDURE (see comment block at end of this file):
//
//   - User obligations change (AUP additions/expansions, narrower opt-out)
//   - User rights change (reduced refund, expanded §6 records-access scope)
//   - Governing law / venue / arbitration provider changes (§10 of ToS)
//   - Fee or billing language changes (§§2, 8 of ToS)
//   - New data category collected (§2 of Privacy)
//   - Reduced retention guarantee or removed privacy commitment (§4, §6 of Privacy)
//   - New vendor processing user data (§5 of Privacy)
//
// Non-material changes (typos, grammar, renumbering, example refreshes,
// vendor name changes that don't change what's processed) do NOT bump.
//
// Mirror this string in:
//   - platform-docs/legal/TERMS_OF_USE.md (header)
//   - platform-docs/legal/PRIVACY_POLICY.md (header)
//   - Maia-WebUI/src/components/legal/TermsOfUse.js (header block JSX)
//   - Maia-WebUI/src/components/legal/PrivacyPolicy.js (header block JSX)

export const LEGAL_VERSION = 'v1.0';

export const LEGAL_LAST_UPDATED = '2026-04-27';

// Hand-curated "what changed" bullets shown in the LegalAcceptanceModal's
// update-mode. EMPTY for v1.0 because this is the baseline ship — there is no
// "previous version" to diff against; pre-ToS users see first-time mode.
//
// Populate at every LEGAL_VERSION bump going forward with 2-5 user-readable
// bullets describing the material changes.
export const WHAT_CHANGED_FROM_PREVIOUS = [];

// Pure helper: returns true if the user must re-accept.
// Strict inequality on purpose — any non-equal version (including future
// versions, draft versions, malformed strings) should re-prompt.
export function needsReAcceptance(acceptedVersion, currentVersion) {
  if (!acceptedVersion) return true;
  return acceptedVersion !== currentVersion;
}

// =============================================================================
// PHASE 2 PROCEDURE — read this BEFORE bumping LEGAL_VERSION.
// =============================================================================
//
// Bumping LEGAL_VERSION fires a blocking modal for every signed-in user on
// next app load. Per platform-docs/legal/TERMS_OF_USE.md §9, material changes
// require 14 days' notice through the service or by email BEFORE the change
// takes effect. The Phase 1 gate ships without this notice infrastructure
// because the baseline version (v1.0) is not a change — it's the establishment
// of the contract.
//
// Before bumping to v2.0+ in any future material-change scenario:
//   1. Add `LEGAL_EFFECTIVE_DATE` constant 14+ days in the future.
//   2. Build a banner component that renders for users with stale-but-not-yet-
//      effective versions during the notice window (NOT a blocking modal).
//   3. Update LegalAcceptanceGate to gate the modal on `Date.now() > effective_date`
//      and render the banner during the notice window.
//   4. Populate WHAT_CHANGED_FROM_PREVIOUS with 2-5 user-readable bullets.
//   5. Mirror the new version string in all four files listed at the top.
//
// Tracked as Phase 2 of phone-agent-issues #40.
